import React, { useCallback, useMemo, useRef } from 'react';
import { ExclamationTriangleIcon, ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Confidence, ImportLineItem, ImportMetadata, HighlightState } from '../types';
import ImportFormHeader from './ImportFormHeader';
import LineItemsTable from './LineItemsTable';
import UndoRedoBar from './UndoRedoBar';

interface ImportFormProps {
  metadata: ImportMetadata;
  onUpdateMetadata: (field: keyof ImportMetadata, value: string) => void;
  lines: ImportLineItem[];
  highlight: HighlightState;
  modifiedFields: Set<string>;
  mergeError: string | null;
  onDismissMergeError: () => void;
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
  onUpdateLine: (id: string, field: keyof ImportLineItem, value: string | number | null) => void;
  onDeleteLine: (id: string) => void;
  onMergeLine: (id: string) => void;
  onSplitLine: (id: string) => void;
  onAddLine: () => void;
  onReorderLines: (activeId: string, overId: string) => void;
  onHoverLine: (id: string | null) => void;
  onClickLine: (id: string | null) => void;
  registerLineRef: (id: string, el: HTMLTableRowElement | null) => void;
  selectedIds: Set<string>;
  onToggleSelect: (id: string, shiftKey: boolean) => void;
  onToggleSelectAll: () => void;
  extraHeaders?: React.ReactNode;
  renderExtraCells?: (line: ImportLineItem) => React.ReactNode;
  extraColumnCount?: number;
  toolbarSlot?: React.ReactNode;
  hiddenColumns?: Set<string>;
}

function hasAlert(line: ImportLineItem): boolean {
  if (line.isTitle) return false;
  if (line.confidence === 'low' || line.confidence === 'medium') return true;
  if (line.cellConfidence) {
    return Object.values(line.cellConfidence).some(
      (c: Confidence | undefined) => c === 'low' || c === 'medium'
    );
  }
  return false;
}

const ImportForm: React.FC<ImportFormProps> = ({
  metadata,
  onUpdateMetadata,
  lines,
  highlight,
  modifiedFields,
  mergeError,
  onDismissMergeError,
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  onUpdateLine,
  onDeleteLine,
  onMergeLine,
  onSplitLine,
  onAddLine,
  onReorderLines,
  onHoverLine,
  onClickLine,
  registerLineRef,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  extraHeaders,
  renderExtraCells,
  extraColumnCount = 0,
  toolbarSlot,
  hiddenColumns,
}) => {
  const alertIds = useMemo(() => lines.filter(hasAlert).map((l) => l.id), [lines]);
  const alertIndexRef = useRef(-1);

  const goToNextAlert = useCallback(() => {
    if (alertIds.length === 0) return;
    alertIndexRef.current = (alertIndexRef.current + 1) % alertIds.length;
    const id = alertIds[alertIndexRef.current];
    onClickLine(id);
  }, [alertIds, onClickLine]);

  return (
    <div className="flex flex-col h-full min-h-0 relative">
      <div className="flex-shrink-0 flex items-center justify-between px-3 py-2 border-b border-gray-200 relative">
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-semibold text-gray-700">Données extraites</h2>
          {toolbarSlot}
        </div>
        <div className="flex items-center gap-2">
          {alertIds.length > 0 && (
            <button
              onClick={goToNextAlert}
              className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-orange-600 bg-orange-50 hover:bg-orange-100 rounded-md transition-colors"
            >
              <ExclamationTriangleIcon className="w-3.5 h-3.5" />
              <span>Alertes</span>
              <span className="text-orange-400">{alertIds.length}</span>
              <ChevronDownIcon className="w-3 h-3 text-orange-400" />
            </button>
          )}
          <UndoRedoBar canUndo={canUndo} canRedo={canRedo} onUndo={onUndo} onRedo={onRedo} />
        </div>
      </div>

      <div className="flex-shrink-0 px-3 py-3">
        <ImportFormHeader metadata={metadata} modifiedFields={modifiedFields} onUpdate={onUpdateMetadata} />
      </div>

      <LineItemsTable
        lines={lines}
        highlight={highlight}
        modifiedFields={modifiedFields}
        onUpdate={onUpdateLine}
        onDelete={onDeleteLine}
        onMerge={onMergeLine}
        onSplit={onSplitLine}
        onAdd={onAddLine}
        onReorder={onReorderLines}
        onHover={onHoverLine}
        onClick={onClickLine}
        registerRef={registerLineRef}
        selectedIds={selectedIds}
        onToggleSelect={onToggleSelect}
        onToggleSelectAll={onToggleSelectAll}
        extraHeaders={extraHeaders}
        renderExtraCells={renderExtraCells}
        extraColumnCount={extraColumnCount}
        hiddenColumns={hiddenColumns}
      />

      {/* Merge error toast */}
      {mergeError && (
        <div className="absolute bottom-4 left-3 right-3 flex items-start gap-2 px-3 py-2.5 bg-red-50 border border-red-200 rounded-lg shadow-lg text-xs text-red-700 animate-fade-in">
          <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0 text-red-400 mt-0.5" />
          <span className="flex-1">{mergeError}</span>
          <button onClick={onDismissMergeError} className="flex-shrink-0 p-0.5 hover:bg-red-100 rounded">
            <XMarkIcon className="w-3.5 h-3.5 text-red-400" />
          </button>
        </div>
      )}
    </div>
  );
};

export default ImportForm;
